import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { usePlatform } from "@/platform/context"
import type {
	ManagedPlugin,
	ManagedPluginID,
	OnboardingState,
	PluginInstallProgress,
} from "@/platform/types"
import { useAuth } from "@/providers/auth"
import { queries } from "@oblique/shared/zero"
import { useZero } from "@rocicorp/zero/react"
import {
	AlertTriangle,
	CheckCircle2,
	CircleDashed,
	ExternalLink,
	Loader2,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { useLocation, useNavigate } from "react-router"
import { isOnboardingDestinationReady } from "./onboarding-navigation"

const initialState: OnboardingState = {
	completed: false,
	step: "welcome",
}

const waitForProject = async (
	zero: ReturnType<typeof useZero>,
	projectID: string,
) => {
	const query = queries.projects.byId(projectID)
	const preload = zero.preload(query)
	void preload.complete.catch(() => undefined)

	try {
		for (let attempt = 0; attempt < 60; attempt += 1) {
			const project = await zero.run(query, { type: "unknown" })
			if (project) return project
			await new Promise((resolve) => setTimeout(resolve, 250))
		}
		return null
	} finally {
		preload.cleanup()
	}
}

export const ElectronOnboarding = ({ children }: { children: ReactNode }) => {
	const platform = usePlatform()
	const location = useLocation()
	const navigate = useNavigate()
	const z = useZero()
	const { login, status, user } = useAuth()
	const authStatus = useRef(status)
	const [state, setState] = useState<OnboardingState | null>(null)
	const [plugins, setPlugins] = useState<ManagedPlugin[]>([])
	const [progress, setProgress] = useState<
		Partial<Record<ManagedPluginID, PluginInstallProgress>>
	>({})
	const [email, setEmail] = useState(platform.defaultEmail ?? "")
	const [password, setPassword] = useState(platform.defaultPassword ?? "")
	const [busy, setBusy] = useState<string | null>(null)
	const [message, setMessage] = useState<string | null>(null)
	const [connectionStatus, setConnectionStatus] = useState<
		"idle" | "checking" | "healthy" | "error"
	>("idle")
	const [connectionMessage, setConnectionMessage] = useState<string | null>(
		null,
	)
	const [sampleProjectID, setSampleProjectID] = useState<string | null>(null)
	const [pendingCompletion, setPendingCompletion] = useState<{
		destinationPathname: string
		state: OnboardingState
	} | null>(null)

	authStatus.current = status

	useEffect(() => {
		void platform.getOnboardingState?.().then((value) => {
			setState(value ?? initialState)
		})
	}, [platform])

	useEffect(
		() =>
			platform.onPluginInstallProgress?.((nextProgress) => {
				setProgress((current) => ({
					...current,
					[nextProgress.pluginID]: nextProgress,
				}))
			}),
		[platform],
	)

	useEffect(() => {
		if (state?.step !== "account" || status !== "authenticated") return
		void platform
			.updateOnboardingState?.({ step: "plugin_install" })
			.then((nextState) => setState(nextState))
	}, [platform, state?.step, status])

	useEffect(() => {
		if (status !== "unauthenticated") return
		setBusy(null)
		setMessage(null)
		setSampleProjectID(null)
		setPendingCompletion(null)
		setConnectionStatus("idle")
		setConnectionMessage(null)
		if (!state?.completed) return
		void platform
			.updateOnboardingState?.({ completed: false, step: "welcome" })
			.then((nextState) => setState(nextState))
	}, [platform, state?.completed, status])

	useEffect(() => {
		if (state?.step !== "plugin_install") return
		void platform.getManagedPlugins?.().then(setPlugins)
	}, [platform, state?.step])

	useEffect(() => {
		// HashRouter commits navigation in a transition. Keep OriginShell unmounted
		// until it can no longer mistake the previous root route for the destination.
		if (
			!pendingCompletion ||
			!isOnboardingDestinationReady(
				location.pathname,
				pendingCompletion.destinationPathname,
			)
		) {
			return
		}

		setState(pendingCompletion.state)
		setPendingCompletion(null)
	}, [location.pathname, pendingCompletion])

	const validateConnection = useCallback(async () => {
		setBusy("connection")
		setConnectionStatus("checking")
		setConnectionMessage("Checking account and plugin catalog…")
		try {
			if (status !== "authenticated") throw new Error("Sign in is required.")
			if (platform.validateOnboardingConnection) {
				await platform.validateOnboardingConnection()
			} else {
				const refreshedPlugins = await platform.refreshManagedPlugins?.()
				if (refreshedPlugins) setPlugins(refreshedPlugins)
			}
			if (authStatus.current !== "authenticated") return false
			setConnectionStatus("healthy")
			setConnectionMessage("Connection healthy.")
			return true
		} catch (error) {
			if (authStatus.current !== "authenticated") {
				setConnectionStatus("idle")
				setConnectionMessage(null)
				return false
			}
			setConnectionStatus("error")
			setConnectionMessage(
				error instanceof Error ? error.message : "Connection check failed.",
			)
			return false
		} finally {
			setBusy(null)
		}
	}, [platform, status])

	useEffect(() => {
		if (state?.step !== "connection_check" || connectionStatus !== "idle") {
			return
		}
		void validateConnection()
	}, [connectionStatus, state?.step, validateConnection])

	if (!state) {
		return (
			<div className="flex h-screen items-center justify-center bg-transparent">
				<Loader2 className="h-5 w-5 animate-spin" />
			</div>
		)
	}

	if (state.completed) return children

	const updateStep = async (step: OnboardingState["step"]) => {
		setMessage(null)
		if (step === "connection_check") setConnectionStatus("idle")
		setState(
			(await platform.updateOnboardingState?.({ step })) ?? { ...state, step },
		)
	}

	const installPlugin = async (plugin: ManagedPlugin) => {
		if (!platform.installManagedPlugin) return
		setBusy(plugin.id)
		setMessage(null)
		try {
			let preparedPlugin = plugin
			if (!preparedPlugin.selectedTargetID && preparedPlugin.targets[0]) {
				preparedPlugin =
					(await platform.selectPluginTarget?.(
						plugin.id,
						preparedPlugin.targets[0].id,
					)) ?? preparedPlugin
			}
			const installed = await platform.installManagedPlugin(preparedPlugin.id)
			setPlugins((current) =>
				current.map((candidate) =>
					candidate.id === installed.id ? installed : candidate,
				),
			)
		} catch (error) {
			setMessage(
				error instanceof Error ? error.message : "Installation failed.",
			)
		} finally {
			setBusy(null)
		}
	}

	const submitLogin = () => {
		if (busy === "login") return
		setBusy("login")
		setMessage(null)
		void login({ email, password })
			.catch((error) =>
				setMessage(error instanceof Error ? error.message : "Sign in failed."),
			)
			.finally(() => setBusy(null))
	}

	const createSampleProject = async () => {
		setBusy("sample")
		setMessage(null)
		try {
			if (connectionStatus !== "healthy") {
				throw new Error("Complete the connection check before continuing.")
			}
			const projectID =
				sampleProjectID ?? (await platform.createOnboardingSampleProject?.())
			if (!projectID) throw new Error("Sample project creation failed.")
			setSampleProjectID(projectID)
			const project = await waitForProject(z, projectID)
			if (!project) {
				throw new Error(
					"The demo project is taking longer than expected. Please try again.",
				)
			}
			if (authStatus.current !== "authenticated") return
			const completed = (await platform.updateOnboardingState?.({
				completed: true,
			})) ?? {
				...state,
				completed: true,
			}
			const destinationPathname = `/projects/${encodeURIComponent(projectID)}`
			navigate(destinationPathname, {
				replace: true,
				state: { projectTitle: project.name },
			})
			setPendingCompletion({ destinationPathname, state: completed })
		} catch (error) {
			if (authStatus.current !== "authenticated") return
			setMessage(
				error instanceof Error
					? error.message
					: "Sample project creation failed.",
			)
		} finally {
			setBusy(null)
		}
	}

	const completeWithoutSample = async () => {
		setMessage(null)
		const completed = (await platform.updateOnboardingState?.({
			completed: true,
		})) ?? {
			...state,
			completed: true,
		}
		setState(completed)
		navigate("/dashboard", { replace: true })
	}

	const progressIndex = [
		"welcome",
		"account",
		"plugin_install",
		"connection_check",
	].indexOf(state.step)

	return (
		<div className="flex h-screen flex-col bg-transparent text-foreground">
			<div className="origin-shell-topbar h-[52px] shrink-0" />
			<div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6 p-8">
				<div className="grid grid-cols-4 gap-2">
					{["Welcome", "Account", "Plugins", "Connection"].map(
						(label, index) => (
							<div className="grid gap-2" key={label}>
								<div
									className={`h-1 rounded-full ${index <= progressIndex ? "bg-primary" : "bg-muted"}`}
								/>
								<span className="text-xs text-muted-foreground">{label}</span>
							</div>
						),
					)}
				</div>

				{state.step === "welcome" ? (
					<section className="grid flex-1 content-center gap-4">
						<h1 className="text-4xl font-semibold">Welcome to Origin</h1>
						<p className="max-w-xl text-muted-foreground">
							Connect your account, install the Blender or Rhino plugin, and
							verify the runtime before opening your first project.
						</p>
						<Button
							className="w-fit"
							onClick={() => void updateStep("account")}
						>
							Get started
						</Button>
					</section>
				) : null}

				{state.step === "account" ? (
					<section className="grid max-w-md gap-4">
						<h1 className="text-2xl font-semibold">Account</h1>
						<p className="text-sm text-muted-foreground">
							Sign in in your browser, or use your email and password here.
						</p>
						<Button
							variant="outline"
							onClick={() => void platform.beginBrowserLogin?.()}
						>
							Sign in in browser
						</Button>
						<form
							className="grid gap-3 rounded-xl border border-border/60 bg-card p-4"
							onSubmit={(event) => {
								event.preventDefault()
								submitLogin()
							}}
						>
							<Input
								autoComplete="email"
								name="email"
								type="email"
								placeholder="you@example.com"
								value={email}
								onChange={(event) => setEmail(event.target.value)}
							/>
							<Input
								autoComplete="current-password"
								name="password"
								type="password"
								value={password}
								onChange={(event) => setPassword(event.target.value)}
							/>
							<Button type="submit" disabled={busy === "login"}>
								{busy === "login" ? "Signing in…" : "Sign in"}
							</Button>
						</form>
						<Button
							variant="ghost"
							className="w-fit"
							onClick={() => void updateStep("welcome")}
						>
							Back
						</Button>
					</section>
				) : null}

				{state.step === "plugin_install" ? (
					<section className="grid gap-4">
						<div>
							<h1 className="text-2xl font-semibold">Plugin installation</h1>
							<p className="text-sm text-muted-foreground">
								Install what you need now. You can change targets later in
								Manage.
							</p>
						</div>
						{plugins.map((plugin) => {
							const installProgress = progress[plugin.id]
							const installed = plugin.installState === "installed"
							return (
								<div
									className="flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4"
									key={plugin.id}
								>
									{installed ? (
										<CheckCircle2 className="h-5 w-5 text-emerald-500" />
									) : (
										<CircleDashed className="h-5 w-5 text-muted-foreground" />
									)}
									<div className="min-w-0 flex-1">
										<strong>{plugin.targetApp}</strong>
										<p className="text-xs text-muted-foreground">
											{installProgress?.step ?? plugin.statusText}
											{installProgress?.progress !== undefined
												? ` ${Math.round(installProgress.progress * 100)}%`
												: ""}
										</p>
										{installProgress?.progress !== undefined ? (
											<div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
												<div
													className="h-full bg-primary"
													style={{
														width: `${installProgress.progress * 100}%`,
													}}
												/>
											</div>
										) : null}
									</div>
									<Button
										disabled={busy !== null || installed}
										onClick={() => void installPlugin(plugin)}
									>
										{installed
											? "Installed"
											: busy === plugin.id
												? "Installing…"
												: "Install"}
									</Button>
								</div>
							)
						})}
						<div className="flex justify-between">
							<Button
								variant="ghost"
								onClick={() => void updateStep("account")}
							>
								Back
							</Button>
							<Button onClick={() => void updateStep("connection_check")}>
								Continue
							</Button>
						</div>
					</section>
				) : null}

				{state.step === "connection_check" ? (
					<section className="grid max-w-xl gap-4">
						<h1 className="text-2xl font-semibold">Connection check</h1>
						<p className="text-sm text-muted-foreground">
							Validate account and plugin catalog access, then create your
							sample project.
						</p>
						{connectionMessage ? (
							<div className="flex items-center gap-2 text-sm">
								{connectionStatus === "checking" ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : connectionStatus === "healthy" ? (
									<CheckCircle2 className="h-4 w-4 text-emerald-500" />
								) : (
									<AlertTriangle className="h-4 w-4 text-amber-500" />
								)}
								<span>{connectionMessage}</span>
							</div>
						) : null}
						{connectionStatus === "error" ? (
							<div className="flex gap-2">
								<Button
									variant="outline"
									disabled={busy !== null}
									onClick={() => void validateConnection()}
								>
									Retry
								</Button>
								<Button
									variant="outline"
									disabled={busy !== null}
									onClick={() =>
										void platform.openExternal?.("https://oblique.tools")
									}
								>
									<ExternalLink className="h-4 w-4" />
									Open Troubleshooting
								</Button>
							</div>
						) : null}
						<div className="flex justify-between">
							<Button
								variant="ghost"
								onClick={() => void updateStep("plugin_install")}
							>
								Back
							</Button>
							<div className="flex gap-2">
								<Button
									variant="outline"
									disabled={busy !== null}
									onClick={() => void completeWithoutSample()}
								>
									Skip for now
								</Button>
								<Button
									disabled={busy !== null || connectionStatus !== "healthy"}
									onClick={() => void createSampleProject()}
								>
									{busy === "sample" ? "Creating…" : "Open demo project"}
								</Button>
							</div>
						</div>
					</section>
				) : null}

				{message ? <p className="text-sm text-destructive">{message}</p> : null}
				{user ? (
					<p className="text-xs text-muted-foreground">
						Signed in as {user.email}
					</p>
				) : null}
			</div>
		</div>
	)
}
